import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Icon, Segment, Table, Transition } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import i18n from '~/components/i18n'
import { StyledTableRow } from '~/styles/table';
import {
  RecipientsAccordion,
  RecipientsAccordionTitle
} from '~/pages/Dairy/Newsletter/style';
import AddicionalRecipients from './AdditionalRecipients'
import { useFormContext, useWatch } from 'react-hook-form'
import NewsletterService from '~/api/services/newsletter'
import notify from '~/components/Notify'
import AmountFilter from './Filters/AmountFilter'
import FarmerGroupQuery from '~/QueryHooks/FarmerGroupsQuery'
import { useAuth } from '~/routes/components/AuthProvider'
import { CLIMATE_CHECK_SLUG, CO2_LFL_SLUG, FAT_PROTEIN_SLUG, MILK_FIXED_SLUG, MILK_STOCK_EXCHANGE } from '~/Constants'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import { FilterWrapper } from './style'
import { ModuleType } from '~/api/services/auth'
import ClimaCheckQuery from '~/QueryHooks/ClimaChQuery'
import { FilterModuleSlug } from '..'

const getTableColumns = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Name'),
    key: 'Name'
  },
  {
    name: i18n.t('Email'),
    key: 'email'
  }
];

interface Props {
  isDraft: boolean
  filterModules: ModuleType[]
  selectedFilter: FilterModuleSlug
}

const watchKeys = [
  'type',
  'farmerGroups',
  'tradingSlotsConfirmed',
  'tradingSlotsOffered',
  'co2Footprints',
  'co2FootprintAnswered',
  'fatProteinTradingSlotsOffered',
  'fatProteinTradingSlotsConfirmed',
  'stockExchangeTradingSlotsOffered',
  'stockExchangeTradingSlotsConfirmed',
  'climateCheckFootprints',
  'climateCheckFootprintAnswered'
]

const Recipients: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { systemData: { modules } } = useAuth()
  const tableColumns = getTableColumns()
  const { isDraft, selectedFilter } = props
  const { control } = useFormContext()
  const hasAnyTradingSlot = props.filterModules.length > 0
  const hasCo2Module = modules.find(module => module.slug === CO2_LFL_SLUG)
  const hasClimateCheck = modules.find(module => module.slug === CLIMATE_CHECK_SLUG)
  
  const [filterModules, setFilterModules] = useState(props.filterModules)

  useEffect(() => {
    setFilterModules(props.filterModules)
  }, [props.filterModules])

  const [
    watchType,
    watchFarmerGroups,
    watchTradingSlotsConfirmed,
    watchTradingSlotsOffered,
    watchCo2FootprintPart,
    watchFootPrintAnswered,
    watchFatProteinOffered,
    watchFatProteinConfirmed,
    watchStockOffered,
    watchStockConfirmed,
    watchClimateCheckFootprints,
    watchClimateCheckFootprintAnswered
  ] = watchKeys.map(key => useWatch({ control, name: key }))

  const {
    isLoading: IsLoadingList,
    data: farmersListOptions,
    refetch: refetchOptions
  } = FarmerGroupQuery.getFarmersGroupsList(['newsletter', selectedFilter])

  const {
    isLoading: isLoadingCo2,
    data: co2ListOptions,
    refetch: refetchCo2Options
  } = CO2ClimateQuery.getCo2List(hasCo2Module && selectedFilter === CO2_LFL_SLUG)

  const {
    isLoading: isLoadingClimaCheck,
    data: climaListOptions,
    refetch: refetchClimaList
  } = ClimaCheckQuery.getClimateCheckList(hasClimateCheck && selectedFilter === CLIMATE_CHECK_SLUG)

  useEffect(() => {
    if (hasCo2Module && selectedFilter === CO2_LFL_SLUG) {
      refetchCo2Options()
    }
    if (hasClimateCheck && selectedFilter === CLIMATE_CHECK_SLUG) {
      refetchClimaList()
    }
    refetchOptions()
  }, [t, hasCo2Module, hasClimateCheck, selectedFilter])

  const amountFilters = {
    [MILK_FIXED_SLUG]: [
      {
        name: 'tradingSlotsOffered',
        label: t('Amount offered')
      },
      {
        name: 'tradingSlotsConfirmed',
        label: t('Amount confirmed')
      }
    ],
    [FAT_PROTEIN_SLUG]: [
      {
        name: 'fatProteinTradingSlotsOffered',
        label: t('Amount offered (F. & E.)')
      },
      {
        name: 'fatProteinTradingSlotsConfirmed',
        label: t('Amount confirmed (F. & E.)')
      }
    ],
    [MILK_STOCK_EXCHANGE]: [
      {
        name: 'stockExchangeTradingSlotsOffered',
        label: t('Amount offered (Stock.)')
      },
      {
        name: 'stockExchangeTradingSlotsConfirmed',
        label: t('Amount confirmed (Stock.)')
      }
    ]

  }

  const [recipients, setRecipients] = useState(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)

  useEffect(() => {
    fetchNewsletterRecipients()
  }, [
    watchTradingSlotsConfirmed,
    watchTradingSlotsOffered,
    watchFarmerGroups,
    watchCo2FootprintPart,
    watchFootPrintAnswered,
    watchFatProteinOffered,
    watchFatProteinConfirmed,
    watchStockConfirmed,
    watchStockOffered,
    watchClimateCheckFootprints,
    watchClimateCheckFootprintAnswered
  ]);

  async function fetchNewsletterRecipients() {
    try {
      let paylod = {}
      if (hasAnyTradingSlot) {
        paylod = {
          tradingSlotsOffered: watchTradingSlotsOffered,
          tradingSlotsConfirmed: watchTradingSlotsConfirmed,
          stockExchangeTradingSlotsConfirmed: watchStockConfirmed,
          stockExchangeTradingSlotsOffered: watchStockOffered,
          fatProteinTradingSlotsOffered: watchFatProteinOffered,
          fatProteinTradingSlotsConfirmed: watchFatProteinConfirmed,
          farmerGroups: watchFarmerGroups
        }
      }
      if (hasCo2Module && selectedFilter === CO2_LFL_SLUG) {
        paylod = {
          ...paylod,
          farmerGroups: watchFarmerGroups,
          co2Footprints: watchCo2FootprintPart,
          co2FootprintAnswered: watchFootPrintAnswered !== undefined ? watchFootPrintAnswered : 0
        };
      }

      if (hasClimateCheck && selectedFilter === CLIMATE_CHECK_SLUG) {
        paylod = {
          ...paylod,
          farmerGroups: watchFarmerGroups,
          climateCheckFootprints: watchClimateCheckFootprints,
          climateCheckFootprintAnswered: watchClimateCheckFootprintAnswered !== undefined ? watchClimateCheckFootprintAnswered : 0
        };
      }

      const { data: recipients } = await NewsletterService.getNewsletterRecipients(paylod)
      setRecipients(recipients);
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
  }

  function recipientsTableHeaders() {
    return <Table.Row>
      {tableColumns?.map(tableColumn => {
        return <Table.HeaderCell key={tableColumn.name}>{tableColumn.name}</Table.HeaderCell>;
      })}
    </Table.Row>;
  }

  function recipientsTableBody() {
    //refactor
    return <Table.Body>
      {recipients?.map(recipient => {
        return <StyledTableRow key={recipient?.id}>
          {<Table.Cell>{recipient.supplierNo}</Table.Cell>}
          {<Table.Cell>{recipient?.name}</Table.Cell>}
          {<Table.Cell>{recipient?.email}</Table.Cell>}
        </StyledTableRow>;
      })}
    </Table.Body>;
  }

  function renderRecipientsTable() {
    return <Table celled padded striped>
      <Table.Header>
        {recipientsTableHeaders()}
      </Table.Header>
      {recipientsTableBody()}
    </Table>;
  }

  function renderReipients() {
    return <>
      <RecipientsAccordion styled>
        <RecipientsAccordionTitle
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          index={0}
          active={isAccordionOpen}>
          <div>{t('Recipients')}:{` ${recipients?.length}`}</div>
          <Icon name={isAccordionOpen ? 'minus' : 'plus'} />
        </RecipientsAccordionTitle>
        <Transition visible={isAccordionOpen} animation='slide down' duration={200}>
          <Accordion.Content active={isAccordionOpen} content={renderRecipientsTable()} />
        </Transition>
      </RecipientsAccordion>
      {watchType && watchType !== 'intern' && <Segment>
        <AddicionalRecipients />
      </Segment>
      }
    </>
  }

  function renderCo2Filters(amountFilter) {
    return (
      <React.Fragment key={amountFilter.slug}>
        <FormFields.Select
          control={control}
          loading={isLoadingCo2}
          label={t('Participated in data collection')}
          name={'co2Footprints'}
          placeholder={t('Select participent')}
          clearable
          value={watchCo2FootprintPart || []}
          options={co2ListOptions?.map((co2Option) => ({ text: co2Option.title, value: co2Option.id }))}
          selection
          selectOnBlur={false}
          multiple
          search
        />
        <FormFields.Checkbox
          control={control}
          defaultValue={0}
          style={{ marginTop: 34 }}
          name={'co2FootprintAnswered'}
          checkboxValue={1}
          label={t('Data collection completed?')}
        />
      </React.Fragment>
    );
  }

  function renderClimateCheckFilters(amountFilter) {
    return (
      <React.Fragment key={amountFilter.slug}>
        <FormFields.Select
          control={control}
          loading={isLoadingClimaCheck}
          label={t('Participated in data collection')}
          name={'climateCheckFootprints'}
          placeholder={t('Select participent')}
          clearable
          value={watchClimateCheckFootprints || []}
          options={climaListOptions?.map((climaOption) => ({ text: climaOption.title, value: climaOption.id }))}
          selection
          selectOnBlur={false}
          multiple
          search
        />
        <FormFields.Checkbox
          control={control}
          defaultValue={0}
          style={{ marginTop: 34 }}
          name={'climateCheckFootprintAnswered'}
          checkboxValue={1}
          label={t('Data collection completed?')}
        />
      </React.Fragment>
    );
  }

  function renderFilters() {
    const onlyCo2Filter = filterModules.length === 1 && (filterModules[0].slug === CO2_LFL_SLUG || filterModules[0].slug === CLIMATE_CHECK_SLUG);
    return (
      <FilterWrapper key={filterModules.map(module => module.slug).join('-')}>
        <FormFields.Select
          control={control}
          label={t('Farmer groups')}
          name='farmerGroups'
          loading={IsLoadingList}
          placeholder={t('Select farmer group')}
          clearable
          rules={!isDraft && { required: { value: true, message: t('Please select farmer group') } }}
          value={watchFarmerGroups || []}
          options={farmersListOptions?.map((farmerGroup) => ({ value: farmerGroup.id, text: farmerGroup.name })) || []}
          selection
          selectOnBlur={false}
          multiple
          search
        />
        {filterModules.map((amountFilter) => {
          const co2Filter = amountFilter.slug === CO2_LFL_SLUG
          const climaCheckFilter = amountFilter.slug === CLIMATE_CHECK_SLUG

          if (co2Filter) {
            return renderCo2Filters(amountFilter)
          }

          if(climaCheckFilter) {
            return renderClimateCheckFilters(amountFilter)
          }
  
          if (!onlyCo2Filter) {
            return amountFilters[amountFilter.slug]?.map((filter) => (
              <AmountFilter key={filter.name} type={amountFilter.slug} name={filter.name} label={filter.label} />
            ));
          }
  
          return null;
        })}
      </FilterWrapper>
    );
  }

  return <>
    {renderFilters()}
    {renderReipients()}
  </>
}
export default Recipients